import React, { useState, useEffect, ReactNode } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import RevisionOperation from './revisionOperation';
import Dialog from '../../layout/Dialog';
import Panel from '../../layout/Panel';
import TabbedDisplay from '../../layout/TabbedDisplay';
import { LayoutOrientations, RevisionType, RevisionDialogMode, AssetTypes } from '../../../globals';
import AssetProperty from '../../properties/AssetProperty';
import DropdownProperty from '../../properties/DropdownProperty';
import Spacer from '../../layout/Spacer';
import AssetUtility from '../../../classes/AssetUtility';
import ListView from '../../listComponent/ListView';
import ListItem from '../../listComponent/ListItem';
import Button from '../../layout/Button';
import { StoreActions } from '../../../state/store';
import StringProperty from '../../properties/StringProperty';
import APIUtility from '../../../classes/APIUtility';
import { refreshJobsFromServer } from '../../filebrowser/FileManager';
import MessagePrompter, { SpecificMessages } from '../../prompts/MessagePrompter';
import { ErrorIcon } from '../../Iconset';
import { TooltipKeys } from "../../tooltips/TooltipKeys";
import AssetsManager from '../../../classes/AssetsManager';
import { useTypedDispatch, useTypedSelector } from '../../../state/hooks';

interface AssetObjectArchiveInfo {
	file_names: string[];
	type?: string;
}

interface TreeItem {
	parentTreeItem?: TreeItem; 
	name?: string; 
	parent?: number; 
	nodeId?: number; 
	children?: any[]; 
	triangles?: number; 
	descendantCount?: number 
}

interface RevisionMetadata {
	names: string[];
	parents: number[];
	triangles?: number[];
}

interface Option { 
	display: string; 
	value: string | null 
}

export default function RevisionDialog(props)
{
	const dispatch = useTypedDispatch();
	const operations = useTypedSelector(state => state.revisionModule.operations);
	const simple = useTypedSelector(state => state.revisionModule.simple);

	const [selectedAssetObject, setSelectedAssetObject] = useState<string>("");
	
	
	const [selectedAssetObjectArchiveInfo, setSelectedAssetObjectArchiveInfo] = useState<AssetObjectArchiveInfo>();
	const [selectedRoot, setSelectedRoot] = useState();
	const [selectedRevision, setSelectedRevision] = useState();
	const [selectedRevisionMetadata, setSelectedRevisionMetadata] = useState<RevisionMetadata>();
	const [newRevisionName, setNewRevisionName] = useState("");
	const [selectedOperation, setSelectedOperation] = useState(-1);


	const selectedAsset = AssetUtility.getAssetName(selectedAssetObject) as string;
	const fileOperations = operations[selectedAsset!];
	const selectedRevisionType = simple[selectedAsset] ? simple[selectedAsset].mode : RevisionType.NONE;

	const [revisionMode, setRevisionMode] = useState(RevisionDialogMode.SIMPLE);

	let revisionNameMap = new Map()

	useEffect(() =>
	{
		if (props.selectedAsset)
		{
			console.log(props.selectedAsset);

			if (props.selectedAsset.type === 'processed_model')
			{
				assetPropertyChanged(props.selectedAsset.nodeId);
			}
			else if (props.selectedAsset.type === 'processed_revision')
			{
				assetPropertyChanged(props.selectedAsset.parent);
				setSelectedRoot(undefined);
				setSelectedRevision(props.selectedAsset.nodeId);
				getRevisionMetadata(props.selectedAsset.nodeId);
			}
		}
	}, []);

	useEffect(() =>
	{
		if (selectedRoot && selectedAssetObject)
		{
			setNewRevisionName(AssetUtility.getAssetName(selectedAssetObject) + " - " + selectedRoot);
		}
	}, [selectedRoot]);

	useEffect(() =>
	{
		if (selectedRevision && selectedAssetObject)
		{
			setNewRevisionName(AssetUtility.getAssetName(selectedAssetObject) + " - " + revisionNameMap.get(selectedRevision));
		}
	}, [selectedRevision]);

	function submitAssetRevision(debugMode = false)
	{
		if (!selectedAssetObject) return;
		if ((selectedAssetObjectArchiveInfo?.file_names ?? []).length > 0)
		{ //if its a zip
			if (!selectedRoot && !selectedRevision)
			{
				MessagePrompter.showError({
					code: 1107,
					title: 'Invalid Optimization Job',
					content: <div>
						Please select a base root file or revision to submit this revision job.
					</div>,
				});
				return;
			}
		}

		const operations = {};
		operations[selectedAsset] = [];
		if (revisionMode === RevisionDialogMode.SIMPLE)
		{
			operations[selectedAsset] = [];
			const convertedOperations = convertSimpleToOperations();
			for (const op of convertedOperations[RevisionType.HIGH])
			{
				if (op.nodes.length > 0)
				{
					operations[selectedAsset].push(op);
				}
			}

			for (const op of convertedOperations[RevisionType.MEDIUM])
			{
				if (op.nodes.length > 0)
				{
					operations[selectedAsset].push(op);
				}
			}

			for (const op of convertedOperations[RevisionType.LOW])
			{
				if (op.nodes.length > 0)
				{
					operations[selectedAsset].push(op);
				}
			}
		}
		else
		{
			operations[selectedAsset] = fileOperations ? [...fileOperations] : [];
		}

		//cleaning up the nodes in the operations for sending
		const assetInfo = AssetsManager.getAssetInfo(selectedAssetObject);

		if (!assetInfo.revisions || assetInfo.revisions.length <= 0)
		{
			const tempOperations: any[] = [];
			for (let i = 0; i < operations[selectedAsset].length; i++)
			{
				const tempOperation = { ...operations[selectedAsset][i] };
				tempOperation.nodes = [];
				tempOperations.push(tempOperation);
			}
			operations[selectedAsset] = tempOperations;
		}
		else
		{
			if (!operations[selectedAsset]) return;
			const tempOperations: any[]  = [];
			for (let i = 0; i < operations[selectedAsset].length; i++)
			{
				const tempOperation = { ...operations[selectedAsset][i] };
				const tempNodes: any[]  = [];
				for (let j = 0; j < operations[selectedAsset][i].nodes.length; j++)
				{
					tempNodes.push(operations[selectedAsset][i].nodes[j].nodeId);
				}
				tempOperation.nodes = tempNodes;
				tempOperations.push(tempOperation);
			}
			operations[selectedAsset] = tempOperations;
		}

		const validOperations: any[] = [];
		for (const operation of operations[selectedAsset])
		{
			if (!operation.command || operation.command === "") continue;
			if (!operation.nodes || operation.nodes.length === 0) operation.nodes = [0];
			validOperations.push({ ...operation });
		}

		if (debugMode === true)
		{

			console.log(validOperations);
			return;
		}

		if (validOperations.length === 0)
		{
			if(!(selectedAssetObjectArchiveInfo?.type === 'zip' && !selectedRevision && selectedRoot))
			{
				MessagePrompter.showMessage({
					icon: <ErrorIcon />,
					title: 'Optimization must have valid operations.',
					content: <div>
						<p>Cannot optimize asset because the job has no valid operations.<br />
							Please make sure that all of the operations have an active command.</p>
						<p><b>Note: If no nodes are selected for the operation the root node will be used.</b></p>
					</div>,
					actions: [{ label: "Close" }],
				});
				return;
			}
		}

		console.log('submitting job');
		APIUtility.createNewModelJob(selectedAssetObject, newRevisionName, operations[selectedAsset], selectedRevision, selectedRoot)
			.then((data) =>
			{
				console.log(data);
				refreshJobsFromServer();
				setRevisionMode(RevisionDialogMode.SIMPLE);
			})
			.catch((error) =>
			{
				console.log(error.response.data.error);
				MessagePrompter.showError({
					code: error.response.status,
					title: '',
					content: <div>
						{error.response.data.error}
					</div>,
				});
				setRevisionMode(RevisionDialogMode.SIMPLE);
			});
	}

	function renderHierarchy(root, drawTarget = true)
	{
		if (!root) return [];

		let selectedMode = selectedRevisionType;
		const decorators: JSX.Element[] = [];

		if (root.triangles && root.triangles > 0)
		{
			decorators.push(<label key={'triangles'}
				tooltip={TooltipKeys.RevisionNodeTriangleCount}>▲ {root.triangles}</label>)
		}

		if (root.descendantCount && root.descendantCount > 1)
		{
			decorators.push(<label key={'descendants'}
				tooltip={TooltipKeys.RevisionNodeDescendantCount}>⌗ {root.descendantCount - 1}</label>)
		}

		if (drawTarget)
		{

			if (simple[selectedAsset] && simple[selectedAsset].nodes && simple[selectedAsset].nodes[root.nodeId] && simple[selectedAsset].nodes[root.nodeId].mode)
			{
				selectedMode = simple[selectedAsset].nodes[root.nodeId].mode;
			}

			if (decorators.length > 0)
			{
				decorators.push(<Spacer key={'spacer'} size={{ x: "5px" }} />)
			}

			decorators.push(<DropdownProperty options={[
				{ display: "None", value: RevisionType.NONE },
				{ display: "Target: Low", value: RevisionType.LOW, tooltip: TooltipKeys.RevisionTargetLow },
				{ display: "Target: Medium", value: RevisionType.MEDIUM, tooltip: TooltipKeys.RevisionTargetMedium },
				{ display: "Target: High", value: RevisionType.HIGH, tooltip: TooltipKeys.RevisionTargetHigh },
			]}
			key={'dropdown'}
			value={selectedMode}
			style={{ width: '150px' }}
			onChangeCallback={(type) =>
			{
				dispatch(StoreActions.revision.setSimpleNode({ file: selectedAsset, nodeId: root.nodeId, mode: type }));
			}} />)
		}

		const listItem =
			<ListItem
				key={root.nodeId}
				label={root.name}
				nodeId={root.nodeId}
				decorators={decorators}
				collapseIconScale={drawTarget ? 0.5 : null}
				parentDecoratorsMapID={"RevisionEditorView"}
			>
				{renderHierarchyChildren(root, drawTarget)}
			</ListItem>

		return listItem;
	}

	function renderHierarchyChildren(parent, drawTarget = true, level = 2)
	{
		const hierarchy: JSX.Element[] = [];
		if (!parent) return [];
		if (!parent.children) return [];

		for (let i = 0; i < parent.children.length; i++)
		{
			const child = parent.children[i];

			let selectedMode = selectedRevisionType;
			const decorators: JSX.Element[] = [];

			if (child.triangles && child.triangles > 0)
			{
				decorators.push(<label key={'triangles'}
					tooltip={TooltipKeys.RevisionNodeTriangleCount}>▲ {child.triangles}</label>)
			}

			if (child.descendantCount && child.descendantCount > 1)
			{
				decorators.push(<label key={'descendants'}
					tooltip={TooltipKeys.RevisionNodeDescendantCount} >⌗ {child.descendantCount - 1}</label>)
			}

			decorators.push(<Spacer key={'spacer'} size={{ x: "5px" }} />);

			if (!drawTarget)
			{
				decorators.push(<Spacer key={'spacer'} size={{ x: "5px" }} />);
				decorators.push(<label key={'h_level'}
					tooltip={TooltipKeys.RevisionNodeLevel}>▚ {level}</label>);
			}
			else
			{
				if (simple[selectedAsset] && simple[selectedAsset].nodes && simple[selectedAsset].nodes[child.nodeId] && simple[selectedAsset].nodes[child.nodeId].mode)
				{
					selectedMode = simple[selectedAsset].nodes[child.nodeId].mode;
				}



				if (decorators.length > 0)
				{
					decorators.push(<Spacer key={'spacer'} size={{ x: "5px" }} />)
				}

				decorators.push(<DropdownProperty options={[
					{ display: "None", value: RevisionType.NONE },
					{ display: "Target: Low", value: RevisionType.LOW },
					{ display: "Target: Medium", value: RevisionType.MEDIUM },
					{ display: "Target: High", value: RevisionType.HIGH },
				]}
				key={'dropdown'}
				value={selectedMode}
				style={{ width: '150px' }}
				onChangeCallback={(type) =>
				{
					dispatch(StoreActions.revision.setSimpleNode({ file: selectedAsset, nodeId: child.nodeId, mode: type }));
				}} />)
			}

			const item =
				<ListItem
					key={child.nodeId}
					label={child.name}
					nodeId={child.nodeId}
					decorators={decorators}
					collapseIconScale={drawTarget ? 0.5 : null}
					parentDecoratorsMapID={"RevisionEditorView"}
				>
					{renderHierarchyChildren(child, drawTarget, level + 1)}
				</ListItem>
			hierarchy.push(item);
		}

		return hierarchy;
	}

	function hierarchySelectionChanged(selectedItems)
	{
		const selection: TreeItem[] = [];
		selectedItems.forEach(item =>
		{
			// console.log(item);
			selection.push({ name: item.label, nodeId: item.nodeId });
		});

		if (selectedAsset && selectedOperation >= 0)
		{
			dispatch(StoreActions.revision.setOperationNodes({ file: selectedAsset, operationIndex: selectedOperation, nodes: selection }));
		}
	}

	function makeHierarchyTree()
	{
		const assetInfo = AssetsManager.getAssetInfo(selectedAssetObject);
		let treeItems: TreeItem[] = [];

		if (!assetInfo.revisions || assetInfo.revisions.length <= 0 || !selectedRevision)
		{
			treeItems.push({
				name: assetInfo.name,
				parent: -1,
				nodeId: 0,
				children: [],
				triangles: 0,
			})
		}
		else if (selectedRevisionMetadata)
		{
			for (let i = 0; i < selectedRevisionMetadata.names.length; i++)
			{
				treeItems.push({
					name: selectedRevisionMetadata.names[i],
					parent: selectedRevisionMetadata.parents[i],
					nodeId: i,
					children: [],
					descendantCount: 1,
					triangles: selectedRevisionMetadata.triangles?.[i] ?? 0,
				})
			}

			for (let i = 0; i < selectedRevisionMetadata.parents.length; i++)
			{
				if (selectedRevisionMetadata.parents[i] === -1) continue;
				const oParent = treeItems[selectedRevisionMetadata.parents[i]];
				const oChild = treeItems[i];
				oChild.parentTreeItem = oParent;
				if (oParent.children) 
				{
					oParent.children.push(oChild);
				}
			}

			function updateObjectCounters(root)
			{
				if (!Array.isArray(root.children) || root.children.length < 1)
				{
					return 1;
				}

				for (let i = 0; i < root.children.length; i++)
				{
					root.descendantCount += updateObjectCounters(root.children[i]);
				}

				return root.descendantCount;
			}

			updateObjectCounters(treeItems[0]);
		}
		else
		{
			treeItems = [{ name: "", parent: -1, nodeId: 0, children: [], triangles: 0, descendantCount: 0 }];
		}

		return { ...treeItems[0] };
	}

	function flattenHierarchyTree(hierarchy, flattenData: { names: string[]; parents: number[] } = { names: [], parents: [] })
	{
		flattenData.names.push(hierarchy.name);
		flattenData.parents.push(hierarchy.parent);
		for (let i = 0; i < hierarchy.children.length; i++)
		{
			flattenData = flattenHierarchyTree(hierarchy.children[i], flattenData);
		}
		return { ...flattenData };
	}

	function getNodesList(metaData: { names: string[] } | null = null)
	{
		const nodeList: { nodeId: number; name: string }[] = [];
		if (!selectedAssetObject) return nodeList;

		const assetInfo = AssetsManager.getAssetInfo(selectedAssetObject);
		if (!assetInfo) return nodeList;

		if (!assetInfo.revisions || assetInfo.revisions.length <= 0)
		{
			nodeList.push({
				nodeId: 0, name: assetInfo.name
			})
		}
		else
		{
			if (metaData)
			{
				for (let i = 0; i < metaData.names.length; i++)
				{
					nodeList.push({ nodeId: i, name: metaData.names[i] })
				}
			}
			else if (selectedRevisionMetadata)
			{
				for (let i = 0; i < selectedRevisionMetadata.names.length; i++)
				{
					nodeList.push({ nodeId: i, name: selectedRevisionMetadata.names[i] })
				}
			}
			else
			{
				nodeList.push({
					nodeId: 0, name: assetInfo.name
				})
			}
		}

		return nodeList;
	}

	function convertSimpleToOperations()
	{
		const newOperations = {}
		if (!selectedAsset || !simple[selectedAsset] || simple[selectedAsset].nodes.length <= 0) return newOperations;

		newOperations[RevisionType.LOW] = [
			{
				nodes: [],
				command: "tesselate",
				params: { level: RevisionType.LOW },
			},
			{
				nodes: [],
				command: "decimate to quality",
				params: { level: RevisionType.LOW },
			}
		];

		newOperations[RevisionType.MEDIUM] = [
			{
				nodes: [],
				command: "tesselate",
				params: { level: RevisionType.MEDIUM },
			},
			{
				nodes: [],
				command: "decimate to quality",
				params: { level: RevisionType.MEDIUM },
			}
		];

		newOperations[RevisionType.HIGH] = [
			{
				nodes: [],
				command: "tesselate",
				params: { level: RevisionType.HIGH },
			},
			{
				nodes: [],
				command: "decimate to quality",
				params: { level: RevisionType.HIGH },
			}
		];


		for (let i = 0; i < simple[selectedAsset].nodes.length; i++)
		{
			const currentNode = simple[selectedAsset].nodes[i];
			const currentMode = currentNode.mode ?? selectedRevisionType;
			if (!newOperations[currentMode]) continue;
			for (const operation of newOperations[currentMode])
			{
				operation.nodes.push({ nodeId: currentNode.nodeId, name: currentNode.name });
			}
		}

		return newOperations;
	}

	function convertOperationsToSimple()
	{
		if (!fileOperations) return;
		dispatch(StoreActions.revision.setSimple({ file: selectedAsset, nodes: getNodesList(), mode: RevisionType.NONE }));
		const automaticRevisions = findOperationsByCommand('tesselate');
		for (const operation of automaticRevisions)
		{
			const mode = fileOperations[operation].params['level'];
			for (const node of fileOperations[operation].nodes)
			{
				dispatch(StoreActions.revision.setSimpleNode({ file: selectedAsset, nodeId: node.nodeId, mode: mode }));
			}
		}
	}

	function revisionModeChanged(mode)
	{
		if (!selectedAsset || !simple[selectedAsset] || simple[selectedAsset].nodes.length <= 0) return;

		if (mode === RevisionDialogMode.SIMPLE)
		{
			if (revisionMode === RevisionDialogMode.ADVANCED) MessagePrompter.showMessage(SpecificMessages.Message_AdvancedOperationsInactive);
			convertOperationsToSimple();
		}
		else
		{
			const automaticRevisions = findOperationsByCommand(['tesselate', 'decimate to quality']);
			for (let i = automaticRevisions.length - 1; i >= 0; i--)
			{
				dispatch(StoreActions.revision.removeItem({ file: selectedAsset, operationIndex: automaticRevisions[i] }));
			}

			const newOperations = convertSimpleToOperations();

			for (const op of newOperations[RevisionType.HIGH])
			{
				if (op.nodes.length > 0)
				{
					dispatch(StoreActions.revision.addItem({ file: selectedAsset, operation: op }));
				}
			}

			for (const op of newOperations[RevisionType.MEDIUM])
			{
				if (op.nodes.length > 0)
				{
					dispatch(StoreActions.revision.addItem({ file: selectedAsset, operation: op }));
				}
			}

			for (const op of newOperations[RevisionType.LOW])
			{
				if (op.nodes.length > 0)
				{
					dispatch(StoreActions.revision.addItem({ file: selectedAsset, operation: op }));
				}
			}
		}
		setRevisionMode(mode);
	}

	function findOperationsByCommand(command: string | string[], param: { name: string; value: any } | null = null)
	{ //param format = {name: 'param name', value: 'param value} - optional
		const result: number[] = [];
		if (!fileOperations || fileOperations.length <= 0 || !command || command.length <= 0) return [];
		for (let i = 0; i < fileOperations.length; i++)
		{
			if (command.includes(fileOperations[i].command))
			{
				if (!param)
				{
					result.push(i);
				}
				else
				{
					if (param && fileOperations[i].params[param.name] && fileOperations[i].params[param.name] === param.value)
					{
						result.push(i);
					}
				}
			}
		}

		return result;
	}

	function clearData()
	{
		if (!selectedAsset || !simple[selectedAsset]) return;
		dispatch(StoreActions.revision.setSimple({ file: selectedAsset, nodes: getNodesList(), mode: RevisionType.NONE }));
	}

	function getArchiveInfo(assetId)
	{
		APIUtility.getAssetArchiveInfo(assetId)
			.then((data) =>
			{
				setSelectedAssetObjectArchiveInfo(data.archive);

			})
			.catch((error) =>
			{
				switch (error.response.data.code)
				{
					case 1101: //Asset is not an archive
						setSelectedAssetObjectArchiveInfo({ file_names: [] });
						break;
					case 1103: //Access to this asset is restricted
						setSelectedAssetObject("");
						MessagePrompter.showError({
							code: error.response.data.code,
							title: error.response.data.message,
							content: <div>
								{error.response.data.info}
							</div>,
						});
						break;
					case 1104: //Asset could not be found
						setSelectedAssetObject("");
						MessagePrompter.showError({
							code: error.response.data.code,
							title: error.response.data.message,
							content: <div>
								{error.response.data.info}
							</div>,
						});
						break;
					case 1105: //Archive could not be found
						setSelectedAssetObject("");
						MessagePrompter.showError({
							code: error.response.data.code,
							title: error.response.data.message,
							content: <div>
								{error.response.data.info}
							</div>,
						});
						break;
					case 1111: //Unspecified error
						setSelectedAssetObject("");
						MessagePrompter.showError({
							code: error.response.data.code,
							title: error.response.data.message,
							content: <div>
								{error.response.data.info}
							</div>,
						});
						break;
					default:
						setSelectedAssetObject("");
						MessagePrompter.showError({
							code: error.response.status,
							title: error.response.statusText,
							content: <div>
								{error.response.data.error}
							</div>,
						});
						break;
				}
			});
	}

	function getRevisionMetadata(revisionId)
	{
		APIUtility.getRevisionMetadata(revisionId)
			.then((data) =>
			{
				setSelectedRevisionMetadata(data);
				dispatch(StoreActions.revision.setSimple({ file: selectedAsset, nodes: getNodesList(data), mode: RevisionType.NONE }));
			})
			.catch((error) =>
			{
				console.error(error);
			});
	}

	const selectedItems = (() =>
	{
		if (selectedOperation < 0 || !fileOperations || !fileOperations[selectedOperation] || fileOperations[selectedOperation].nodes.length === 0) return new Set();
		const selection = new Set();
		for (let i = 0; i < fileOperations[selectedOperation].nodes.length; i++)
		{
			selection.add(fileOperations[selectedOperation].nodes[i].nodeId);
		}
		return selection;
	})();

	const drawOperations = (() =>
	{
		if (!selectedAsset || !fileOperations) return [];
		const operationsToDraw: JSX.Element[] = [];
		for (let i = 0; i < fileOperations.length; i++)
		{
			operationsToDraw.push(<RevisionOperation
				key={fileOperations[i].id}
				index={i} file={selectedAsset}
				onNodeSelection={() => { setSelectedOperation(i) }}
				operations={fileOperations}
			/>
			)
		}
		return operationsToDraw;
	})();

	const drawSimpleMode = (() =>
	{
		if (revisionMode !== RevisionDialogMode.SIMPLE) return <div tabheader={"Simple"}></div>;
		console.log('drawing simple mode');
		if (selectedAsset && (!simple[selectedAsset]))
		{ //|| simple[selectedAsset].nodes.length !== getNodesList().length
			console.log('creating new simple mode');
			dispatch(StoreActions.revision.setSimple({ file: selectedAsset, nodes: getNodesList(), mode: RevisionType.NONE }));
		}

		return (
			<Panel tabheader={"Simple"} style={{ outline: 'solid 2px black' }}>
				<Panel orientation={LayoutOrientations.Horizontal}>
					<Panel className="centerContent">{selectedAsset ? selectedAsset : ""}</Panel>
					<Panel className="centerContent">
						<Button
							label={'Clear Data'}
							category={'Revision Editor'}
							data-name={'clear-data'}
							onClick={() => { clearData() }}
							// iconRenderer={<ResetIcon/>}

						/>
					</Panel>
				</Panel>
				<Panel style={{ height: "85%", outline: 'solid 1px black' }}>
					<ListView
						parentDecoratorsMapID={"RevisionEditorView"}
					>
						{selectedAsset ? renderHierarchy(makeHierarchyTree()) : ""}
					</ListView>
				</Panel>
			</Panel>
		);
	})();

	const drawAdvancedMode = (() =>
	{
		if (revisionMode !== RevisionDialogMode.ADVANCED) return <div tabheader={"Advanced"}></div>;
		// console.log('drawing advanced mode');
		return (
			<Panel tabheader={"Advanced"} style={{ outline: 'solid 2px black' }}>
				<Panel orientation={LayoutOrientations.Horizontal} style={{ maxHeight: '40px', minHeight: '40px' }}>
					<Panel className="centerContent">{selectedAsset ? selectedAsset : ""}</Panel>
					<Spacer size={{ x: "10px" }} />
					<Panel className="centerContent" orientation={LayoutOrientations.Horizontal} style={{ minWidth: '55%', maxWidth: '55%' }}>{"Operations"}</Panel>
				</Panel>
				<Panel orientation={LayoutOrientations.Horizontal} style={{ outline: 'solid 1px black' }}>
					<Panel style={{ outline: 'solid 1px black' }}>
						<ListView
							style={{ maxHeight: '355px' }}
							selectedItems={selectedItems}
							selectionChangedCallback={hierarchySelectionChanged}
							multiSelectEnabled={true}
							parentDecoratorsMapID={"RevisionEditorView"}
						>
							{selectedAsset ? renderHierarchy(makeHierarchyTree(), false) : ""}
						</ListView>
					</Panel>
					<Spacer size={{ x: "10px" }} />
					<Panel style={{ minWidth: '55%', maxWidth: '55%', outline: 'solid 1px black' }}>
						<Panel>
							<ListView style={{ maxHeight: '355px' }}>
								{drawOperations}
							</ListView>
						</Panel>
						<div className={"listWindowAddItemButtonWrapper"}>
							<Button
								data-name={"revision-add-operation"}
								category={"Revision"}
								label={"Add Operation"}
								onClick={() =>
								{
									if (!selectedAsset) return;
									dispatch(StoreActions.revision.addItem({
										file: selectedAsset,
									}));
								}}
							/>
						</div>
					</Panel>
				</Panel>
			</Panel>
		);
	})();

	const generateRootFileSelection = (() =>
	{
		if (!selectedAssetObject || selectedRevision)
		{
			return null;
		}

		if (!selectedAssetObjectArchiveInfo)
		{
			return (<div style={{ width: '100%', textAlign: 'center' }}>Getting Information...</div>);
		}

		let result: JSX.Element | null = null;
		if (selectedAssetObjectArchiveInfo?.file_names.length <= 0)
		{
			result = null;
		}
		else
		{
			const opts: Option[] = [];
			for (const n of selectedAssetObjectArchiveInfo.file_names)
			{
				opts.push({ display: n, value: n })
			}
			result = <DropdownProperty options={opts}
				label={"Root File"}
				key={'dropdown-root'}
				value={selectedRoot}
				// style={{width:'150px'}}
				onChangeCallback={(root) =>
				{
					setSelectedRevision(undefined);
					setSelectedRoot(root);
				}} />
		}

		return result;
	})();

	const generateRevisionSelection = (() =>
	{
		if (!selectedAssetObject)
		{
			return null;
		}

		const assetInfo = AssetsManager.getAssetInfo(selectedAssetObject);
		if (!assetInfo.revisions || assetInfo.revisions.length <= 0)
		{
			return null;
		}

		const opts: Option[] = [];
		revisionNameMap = new Map();
		opts.push({ display: "None", value: null });
		for (const r of assetInfo.revisions)
		{
			opts.push({ display: r.name, value: r.id });
			revisionNameMap.set(r.id, r.name);
		}

		return <DropdownProperty options={opts}
			label={"Source file"}
			key={'dropdown-revisions'}
			value={selectedRevision}
			// style={{width:'150px'}}
			onChangeCallback={(revision) =>
			{
				setSelectedRoot(undefined);
				setSelectedRevision(revision);
				getRevisionMetadata(revision);
			}} />
	})();

	function onSD()
	{
		if (props.onShutdown)
		{
			props.onShutdown();
		}
	}

	function assetPropertyChanged(assetId)
	{
		console.log(AssetsManager.getAssetInfo(assetId));
		clearData();
		setSelectedAssetObject(assetId);
		if (assetId)
		{
			getArchiveInfo(assetId);
			setNewRevisionName(AssetsManager.getAssetInfo(assetId).name + " new");
			setSelectedRoot(undefined);
			setSelectedRevision(undefined);
		}
	}

	return (
		<Dialog
			title={"Optimize 3D Asset"}
			className={"assetSwapDialogRoot"}
			contentStyle={{ overflow: 'hidden' }}
			data-name={"asset-revision-dialog-root"}
			actions={[
				{
					label: "Confirm",
					onClick: () => submitAssetRevision(),
				},
				{
					label: "Cancel",
				}
			]}
			onShutdown={onSD}
		>
			<AssetProperty
				label={"Select Asset"}
				type={AssetTypes.Model}
				libraryOnly={true}
				value={selectedAssetObject}
				onChangeCallback={(asset) =>
				{
					assetPropertyChanged(asset);
				}} />

			{generateRevisionSelection}
			{generateRootFileSelection}

			<StringProperty
				label={"New Name"}
				value={newRevisionName}
				onChangeCallback={(v) =>
				{
					setNewRevisionName(v);
				}}
			/>
			<Spacer size={{ y: "20px" }} />
			<Panel size={{ x: "900px", y: "400px" }}>
				{selectedAssetObject ?
					<TabbedDisplay evenTabs value={revisionMode} onTabChangedCallback={revisionModeChanged}>
						{drawSimpleMode}
						{drawAdvancedMode}
					</TabbedDisplay>
					: <></>
				}
			</Panel>
		</Dialog>
	);
}
